<script setup lang="ts">
import {
 computed, toRefs, getCurrentInstance,
} from 'vue';
import { DataOptions } from 'vuetify';

interface DataPagination {
  page: number;
  pageCount: number;
  itemsPerPage: number;
  itemsLength: number;
}

interface SiffletifyDataFooterProps {
  footer: { options: DataOptions & { itemsPerPageOptions?: number[] }, pagination: DataPagination },
  itemsPerPageOptions?: number[],
  scrollTo?: string,
  maximumVisible?: number
}
const props = defineProps({
  footer: null,
  itemsPerPageOptions: null,
  scrollTo: null,
  maximumVisible: null
});

const {
 footer, itemsPerPageOptions, scrollTo, maximumVisible,
} = toRefs(props);

type SiffletifyDataFooterEmits = {
  (event: 'update', options: DataOptions & { itemsPerPageOptions?: number[] | undefined }): void,
}
const emits = defineEmits(["update"]);

const viewList = [5, 10, 25, 50, 100];

const viewOptions = computed(() => {
  if (itemsPerPageOptions?.value) return itemsPerPageOptions.value;
  if (footer.value.options.itemsPerPageOptions) return footer.value.options.itemsPerPageOptions;
  return viewList;
});

const total = computed(() => footer.value?.pagination?.itemsLength);

const viewFormatted = computed(() => {
  const id = viewOptions.value.findIndex((item: number) => item > total.value);
  switch (true) {
    case (id === -1):
      return viewOptions.value;
    case (id === 0):
      return [viewOptions.value[0]];
    case (id > 0):
      return viewOptions.value.slice(0, id + 1);
    default:
      return viewOptions.value;
  }
});

const isLastPage = computed(() => footer.value.pagination.page === footer.value.pagination.pageCount);

const from = computed(() => ((footer.value.pagination.page - 1) * footer.value.pagination.itemsPerPage) + 1);

const to = computed(() => (isLastPage.value
  ? total.value
  : (footer.value.pagination.page - 1) * footer.value.pagination.itemsPerPage + footer.value.pagination.itemsPerPage));

const countOfTotal = computed(() => `${from.value}-${to.value} of ${total.value}`);

const maxVisible = computed(() => maximumVisible?.value ?? 7);

const totalVisible = computed(() => (total.value < maxVisible.value ? total.value - 1 : maxVisible.value));

const disablePagination = computed(() => total.value <= viewFormatted.value[0]);

function update() {
  // If the current page is out of range, reset to 1
  const maxPage = Math.ceil(total.value / footer.value.options.itemsPerPage);
  if (footer.value.options.page > maxPage) {
    footer.value.options.page = 1;
  }
  emits('update', footer.value.options);
  // First try to find a scrollable container
  const container = (getCurrentInstance()?.proxy?.$el as HTMLElement)?.closest('.scrollable');
  if (container) {
    container.scrollTop = 0;
  } else if (scrollTo?.value) {
    // Fall back to the original scrollTo behavior
    (getCurrentInstance()?.proxy as any)?.$vuetify.goTo(scrollTo.value === 'top' ? 0 : `#${scrollTo.value}`);
  }
  (getCurrentInstance()?.proxy as any)?.$forceUpdate();
}

function input(event: number) {
  footer.value.options.page = event;
  update();
}
</script>

<template lang="pug">
v-toolbar(flat v-if="!disablePagination")
  v-spacer
  v-pagination(
    :value.sync="footer.pagination.page"
    :length="footer.pagination.pageCount"
    :total-visible="totalVisible"
    :dark="$vuetify.theme.dark"
    @input="input($event)"
    class="elevation-0"
  )
  v-spacer
  span.d-flex.justify-end.align-center
    .d-flex.justify-end.caption.font-weight-medium(data-track="pagination-count") {{ countOfTotal }}
    v-divider.mx-4( vertical )
    v-select(
      v-model="footer.options.itemsPerPage"
      :items="viewFormatted"
      dense
      variant="solo"
      class="view-select"
      hide-details="auto"
      @change="update"
      :menu-props="{ bottom: true, offsetY: true }"
      data-track="pagination-view"
    )
      template(v-slot:selection='{ item }')
        .caption.font-weight-medium {{ $tc('design.view', 1) }} {{ item }}
</template>

<style lang="scss">
.view-select {
  width: 85px;

  .v-input__slot::before,
  .v-input__slot::after {
    border-style: none !important;
  }

  .v-input__icon .v-icon.v-icon {
    font-size: 14px;
    margin-top: 6px;
    margin-right: 32px;
    color: map-deep-get($grey, "base") !important;
  }
}
</style>
